import React, {useCallback, useMemo, useState} from 'react';
import {Box, Dialog as MuiDialog, DialogActions, DialogContent, DialogProps, DialogTitle, LinearProgress, Tooltip, Typography} from '@mui/material';
import Button from '../../../../../../../components/base/button/button';
import Form, {MuiRFF} from '../../../../../../../components/form';
import Grid2 from '@mui/material/Unstable_Grid2';
import * as Yup from 'yup';
import ValidateMessages from '../../../../../../../../core/models/static/texts/vallidate-messages';
import useIsMounted from '../../../../../../../hooks/use-is-mounted';
import {DashboardEntry, DashboardWidgetEntry, DashboardWidgetLayoutConfig} from "../../types";
import Api from "../../../../../../../../core/services/api/api";
import ImportingLottieAnimation from "../../../../../../../components/app-specific/lottie-animations/importing";
// @ts-ignore
import {ReactComponent as CheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox.svg";
// @ts-ignore
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox-active.svg";
import Utils from "../../../../../../../../core/services/utils/utils";
import {useOperatorInformation} from "../../../../../../../hooks/use-operator-information";

export type DashboardManipulationVariant = 'duplicate' | 'import' | 'export';

interface DashboardManipulationDialogProps {
    open: boolean;
    onClose: (dashboard?: DashboardEntry) => void;
    dashboard: DashboardEntry | null;
    variant: DashboardManipulationVariant;
}

const schema = Yup.object().shape({
    name: Yup.string().trim().required(ValidateMessages.required),
    setAsDefault: Yup.boolean().required(ValidateMessages.required).default(false),
    assignGroups: Yup.boolean().required(ValidateMessages.required).default(false),
    assignLayoutConfig: Yup.boolean().required(ValidateMessages.required).default(false),
    assignWidgetsCustomFilter: Yup.boolean().required(ValidateMessages.required).default(false),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

interface Progress {
    message?: string;
    percentage?: number;
}


const DashboardManipulationDialog = ({open, onClose, dashboard, variant}: DashboardManipulationDialogProps) => {
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState<Progress>({message: undefined, percentage: 0});
    const isMounted = useIsMounted();
    const operatorInformation = useOperatorInformation();

    const titlePrefix = useMemo(() => {
        if (variant === 'import') return ' - Imported';
        if (variant === 'duplicate') return ' - Copy';
        if (variant === 'export') return ' - Exported';
        return '';
    }, [variant]);

    const initialValues = useMemo(() => ({
        name: `${dashboard?.title ?? ''}${titlePrefix}`,
        assignLayoutConfig: true,
        assignWidgetsCustomFilter: true,
        assignGroups: true,
        setAsDefault: false,
    }), [dashboard?.title, titlePrefix])

    /**
     * Exports the dashboard as a json file.
     */
    const exportDashboard = useCallback((values) => {
        const exportData: Partial<DashboardEntry> = {
            id: dashboard?.id,
            title: values.name,
            isDefault: dashboard?.isDefault ?? false,
            newWidgets: dashboard?.newWidgets?.map(w => ({
                ...w,
                parameters: [],
                customFilter: values.assignWidgetsCustomFilter ? w.customFilter : undefined,
            })) ?? [],
            uiComponentData: values.assignLayoutConfig ? dashboard?.uiComponentData : undefined,
            groupIds: values.assignGroups ? dashboard?.groupIds : [],
        };

        Utils.exportJsonFile(exportData, {fileTitle: `${values.name}.json`})
        onClose();
    }, [dashboard?.groupIds, dashboard?.id, dashboard?.isDefault, dashboard?.newWidgets, dashboard?.uiComponentData, onClose])

    /**
     * Creates a new dashboard based on the provided values.
     */
    const createDashboard = useCallback(async (values) => {
        setProcessing(true);

        let actionsDone = 0;
        let actionsLength = 0;

        actionsLength++ // dashboard creation
        if (values.assignLayoutConfig)
            actionsLength++ // setting dashboard uiComponentData
        if (values.assignGroups)
            actionsLength++ // Update dashboard groups
        if (values.setAsDefault)
            actionsLength++ // setting dashboard as default
        for (let i = 0; i < (dashboard?.newWidgets ?? []).length; i++) {
            const widget = dashboard?.newWidgets?.[i];
            if (!widget) {
                continue; // we just skip it
            }
            actionsLength++; // one for widget creation
            if (values.assignWidgetsCustomFilter) {
                if (widget.customFilter)
                    actionsLength++; // one for widget config updates (setting customFilter)
            }
        }


        // Create dashboard
        setProgress({message: 'Creating dashboard', percentage: Math.floor((actionsDone++ / actionsLength) * 100)});
        const createDashboardResponse = await Api.createCustomDashboard({title: values.name});
        if (!isMounted())
            return;
        if (!createDashboardResponse?.resultFlag) {
            setProcessing(false);
            return;
        }
        const dashboardRecord = {
            id: createDashboardResponse.data.id,
            title: createDashboardResponse.data.title,
            isDefault: false,
            ownerName: operatorInformation?.name ?? "",
            isOwnedByMe: true,
            uiComponentData: '',
            newWidgets: [],
            groupIds: []
        } as DashboardEntry;

        // set as default dashboard
        if (values.setAsDefault) {
            setProgress({message: 'Setting as default dashboard', percentage: Math.floor((actionsDone++ / actionsLength) * 100)});
            const setDefaultResponse = await Api.setDefaultCustomDashboard({dashboardId: dashboardRecord.id});
            if (!isMounted())
                return;
            if (setDefaultResponse?.resultFlag) {
                dashboardRecord.isDefault = true;
            }
        }

        // Add dashboard widgets

        const widgetConnectionIdMap = new Map<string, string>();

        const newWidgetsLength = dashboard?.newWidgets?.length ?? 0;
        for (let i = 0; i < newWidgetsLength; i++) {
            const widget = dashboard?.newWidgets?.[i];
            if (!widget) {
                continue;
            }
            // create widget
            setProgress({message: `Adding widget ${i + 1}/${dashboard?.newWidgets?.length}`, percentage: Math.floor((actionsDone++ / actionsLength) * 100)});
            const createWidgetResponse = await Api.addCustomDashboardWidget({
                widgetId: widget.id,
                customTitle: widget.customTitle,
                dashboardId: dashboardRecord.id,
            });
            if (!isMounted())
                return;

            if (createWidgetResponse?.resultFlag) {
                widgetConnectionIdMap.set(widget.connectionId, createWidgetResponse.data?.id ?? '');
                dashboardRecord.newWidgets.push({
                    ...widget,
                    connectionId: createWidgetResponse.data?.id,
                    customTitle: createWidgetResponse.data?.title,
                    customFilter: createWidgetResponse.data?.customFilter,
                } as DashboardEntry['newWidgets'][number]);
            }
        }

        // Update dashboard widget configs

        if (values.assignWidgetsCustomFilter) {
            for (let i = 0; i < dashboardRecord.newWidgets.length; i++) {
                const newWidget = dashboardRecord.newWidgets[i];
                const oldWidget = dashboard?.newWidgets?.find(w => widgetConnectionIdMap.get(w.connectionId) === newWidget.connectionId);
                if (!oldWidget || !oldWidget.customFilter) {
                    continue;
                }
                // update widget config
                setProgress({message: `Configuring widget ${i + 1}/${dashboardRecord.newWidgets.length}`, percentage: Math.floor((actionsDone++ / actionsLength) * 100)});
                const response = await Api.updateCustomDashboardWidget({
                    id: newWidget.connectionId,
                    customTitle: newWidget.customTitle,
                    customFilter: oldWidget.customFilter ?? '',
                });
                if (!isMounted())
                    return;
                if (response?.resultFlag) {
                    dashboardRecord.newWidgets[i] = {
                        ...newWidget,
                        customFilter: oldWidget.customFilter ?? '',
                    } as DashboardEntry['newWidgets'][number];
                }
            }
        }

        if (values.assignLayoutConfig) {
            // Update dashboard uiComponentData
            setProgress({message: 'Setting dashboard configuration', percentage: Math.floor((actionsDone++ / actionsLength) * 100)});
            // Prepare the new UIComponentData since we need to map old connectionIds to new ones
            const uiComponentData = (dashboard?.uiComponentData ? JSON.parse(dashboard?.uiComponentData ?? "") : null) as Record<DashboardWidgetEntry['id'], DashboardWidgetLayoutConfig> | null;
            let newComponentData: Record<DashboardWidgetEntry['id'], DashboardWidgetLayoutConfig> | null = null;
            if (uiComponentData) {
                newComponentData = {};
                for (const [oldConnectionId, newConnectionId] of widgetConnectionIdMap.entries()) {
                    if (uiComponentData[oldConnectionId]) {
                        newComponentData[newConnectionId] = uiComponentData[oldConnectionId];
                    }
                }
                const updateDashboardResponse = await Api.updateCustomDashboardUIData({
                    dashboardId: dashboardRecord.id,
                    uiComponentData: JSON.stringify(newComponentData),
                });
                if (!isMounted())
                    return;
                if (updateDashboardResponse?.resultFlag) {
                    dashboardRecord.uiComponentData = JSON.stringify(newComponentData);
                }
            }
        }

        if (values.assignGroups) {
            // Update dashboard groups.
            setProgress({message: 'Setting dashboard groups', percentage: Math.floor((actionsDone++ / actionsLength) * 100)});
            const updateDashboardGroupsResponse = await Api.setCustomDashboardGroups({
                dashboardId: dashboardRecord.id,
                groupIds: dashboard?.groupIds ?? [],
            });
            if (!isMounted())
                return;
            if (updateDashboardGroupsResponse?.resultFlag) {
                dashboardRecord.groupIds = dashboard?.groupIds ?? [];
            }
        }
        setProgress({message: 'Completed', percentage: 100});
        setProcessing(false);
        onClose(dashboardRecord);
    }, [dashboard?.groupIds, dashboard?.newWidgets, dashboard?.uiComponentData, isMounted, onClose, operatorInformation?.name])

    /**
     * Handles creating a new dashboard or exporting the dashboard json
     */
    const handleSubmit = useCallback(async (values: any) => {
        switch (variant) {
            case "export":
                exportDashboard(values);
                break;
            case "import":
            case "duplicate":
                await createDashboard(values);
                break;
        }
    }, [createDashboard, exportDashboard, variant]);

    /**
     * Handles closing the dialog without saving
     */
    const handleClose = useCallback<Required<DialogProps>['onClose']>((_, reason) => {
        if (processing && reason === 'backdropClick') {
            return;
        }
        onClose();
        onClose();
    }, [onClose, processing]);

    const displayTexts = useMemo(() => {
        if (variant === 'export') {
            return {
                title: `Export ${dashboard?.title ?? 'Dashboard'}`,
                description: 'Please provide a name for the exported dashboard and select the options to include.',
                layoutLabel: 'Include Layout Configuration',
                layoutHelper: 'If checked, the layout and size of widgets will be included.',
                filtersLabel: 'Include Widgets Custom Filters',
                filtersHelper: 'If checked, the custom filters applied to widgets will be included.',
                groupsLabel: 'Include Dashboard Groups',
                groupsHelper: 'If checked, the group associations of the dashboard will be included.',
                button: processing ? "Processing..." : "Export",
            };
        } else if (variant === 'import') {
            return {
                title: `Import ${dashboard?.title ?? 'Dashboard'}`,
                description: 'Please provide a name for the imported dashboard and select the options below.',
                layoutLabel: 'Restore Layout Configuration',
                layoutHelper: 'If checked, the layout and size of widgets will be restored to the new dashboard.',
                filtersLabel: 'Restore Widgets Custom Filters',
                filtersHelper: 'If checked, the custom filters applied to widgets will be restored to the new dashboard.',
                groupsLabel: 'Restore Dashboard Groups',
                groupsHelper: 'If checked, the group associations of the dashboard will be restored to the new dashboard.',
                button: processing ? "Processing..." : "Import",
            };
        }

        return {
            title: `Duplicate ${dashboard?.title ?? 'Dashboard'}`,
            description: 'Please provide a name for the duplicated dashboard and select the options below.',
            layoutLabel: 'Copy Layout Configuration',
            layoutHelper: 'If checked, the layout and size of widgets will be copied to the new dashboard.',
            filtersLabel: 'Copy Widgets Custom Filters',
            filtersHelper: 'If checked, the custom filters applied to widgets will be copied to the new dashboard.',
            groupsLabel: 'Copy Dashboard Groups',
            groupsHelper: 'If checked, the group associations of the dashboard will be copied to the new dashboard.',
            button: processing ? "Processing..." : "Duplicate",
        };
    }, [dashboard?.title, processing, variant]);


    return (
        <MuiDialog
            open={open}
            onClose={handleClose}
            maxWidth={processing ? 'md' : "sm"}
            fullWidth
            disableEscapeKeyDown={processing}
        >
            <DialogTitle>
                {displayTexts.title}
            </DialogTitle>
            <Form
                onSubmit={handleSubmit}
                validate={validate}
                formProps={{id: 'manipulation-dashboard-form'}}
                initialValues={initialValues}
            >
                {({values}) => {
                    return (
                        <>
                            <DialogContent dividers sx={{paddingBlock: 2}}>
                                {
                                    !processing &&
                                    <Grid2 container spacing={2}>
                                        <Grid2 xs={12}>
                                            <Typography variant={'body2'} sx={{marginBlockEnd: 3}}>
                                                {displayTexts.description}
                                            </Typography>
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <MuiRFF.TextField
                                                name={'name'}
                                                label="Dashboard Name"
                                                required={required.name}
                                                fullWidth
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <MuiRFF.Checkboxes
                                                color={'primary'}
                                                icon={<CheckboxIcon/>}
                                                checkedIcon={<ActiveCheckboxIcon/>}
                                                data={{
                                                    value: values.assignLayoutConfig,
                                                    label: displayTexts.layoutLabel,
                                                }}
                                                helperText={displayTexts.layoutHelper}
                                                name={'assignLayoutConfig'}
                                                required={required.assignLayoutConfig}
                                                formControlLabelProps={{
                                                    sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                                }}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <MuiRFF.Checkboxes
                                                color={'primary'}
                                                icon={<CheckboxIcon/>}
                                                checkedIcon={<ActiveCheckboxIcon/>}
                                                data={{
                                                    value: values.assignWidgetsCustomFilter,
                                                    label: displayTexts.filtersLabel,
                                                }}
                                                helperText={displayTexts.filtersHelper}
                                                name={'assignWidgetsCustomFilter'}
                                                required={required.assignWidgetsCustomFilter}
                                                formControlLabelProps={{
                                                    sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                                }}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12}>
                                            <MuiRFF.Checkboxes
                                                color={'primary'}
                                                icon={<CheckboxIcon/>}
                                                checkedIcon={<ActiveCheckboxIcon/>}
                                                data={{
                                                    value: values.assignGroups,
                                                    label: displayTexts.groupsLabel,
                                                }}
                                                helperText={displayTexts.groupsHelper}
                                                name={'assignGroups'}
                                                required={required.assignGroups}
                                                formControlLabelProps={{
                                                    sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                                }}
                                            />
                                        </Grid2>
                                        {
                                            variant !== 'export' &&
                                            <Grid2 xs={12}>
                                                <MuiRFF.Checkboxes
                                                    color={'primary'}
                                                    icon={<CheckboxIcon/>}
                                                    checkedIcon={<ActiveCheckboxIcon/>}
                                                    data={{
                                                        value: values.setAsDefault,
                                                        label: 'Set as My Default Dashboard',
                                                    }}
                                                    helperText={'If checked, the new dashboard will be set as your default dashboard.'}
                                                    name={'setAsDefault'}
                                                    required={required.setAsDefault}
                                                    formControlLabelProps={{
                                                        sx: {marginBlockEnd: 0, marginInlineEnd: 0,},
                                                    }}
                                                />
                                            </Grid2>
                                        }
                                    </Grid2>
                                }
                                {
                                    processing &&
                                    <Grid2 container spacing={2}>
                                        <Grid2 xs={12}>
                                            <div className={'d-flex flex-column py-4'}>
                                                <ImportingLottieAnimation width={'80%'} height={'50vh'} className={''}/>
                                                <Tooltip open={true} placement={'top'} arrow title={progress.message}>
                                                    <div className={'d-flex flex-column w-100 py-3 align-items-center'}>
                                                        <LinearProgress
                                                            className={'w-80'}
                                                            variant={'buffer'}
                                                            color={'secondary'}
                                                            value={progress.percentage}
                                                            valueBuffer={progress.percentage}
                                                        />
                                                        <p className={'identifier'}>{`${progress.percentage}%`}</p>
                                                    </div>
                                                </Tooltip>
                                                <Typography variant={'body2'} className={'mb-2'} textAlign={'center'}>
                                                    Please do not close this dialog until the process is complete.
                                                </Typography>
                                            </div>
                                        </Grid2>
                                    </Grid2>
                                }
                            </DialogContent>
                            <DialogActions className="p-0">
                                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                                    <Button
                                        disabled={processing}
                                        variant="dialog"
                                        color="primary"
                                        onClick={() => onClose()}
                                        sx={{borderRadius: '0 10px 0 10px'}}
                                    >
                                        Close
                                    </Button>
                                    <Button
                                        disabled={processing}
                                        variant="dialog"
                                        color="primary"
                                        type="submit"
                                        form="manipulation-dashboard-form"
                                        sx={{borderRadius: '10px 0 10px 0'}}
                                    >
                                        {displayTexts.button}
                                    </Button>
                                </Box>
                            </DialogActions>
                        </>
                    );
                }}
            </Form>
        </MuiDialog>
    );
};

export default DashboardManipulationDialog;
